import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { AI_ROLE_EXPLANATION, AI_ONE_LINER } from '../constants/aiPolicy';
import { api } from '../lib/api';
import { Footer } from '../components/Footer';

type Mode = 'login' | 'register' | 'guest_judge_apply';

const PILLARS = [
  { k: '01', icon: '🎤', title: 'Studio', path: '/app/studio', body: 'AI beats and voice engine. Turn scratch ideas into finished records. You write the words; AI handles the production.' },
  { k: '02', icon: '⚔️', title: 'Arena & Battles', path: '/app/arena', body: 'Month-long tournament with up to 34 entries. Weekly eliminations weed out the lowest scores, leading into the 4th week live finale show.' },
  { k: '03', icon: '📈', title: 'Feed & Charts', path: '/app/wave', body: 'Video-first discovery. No follower-gated reach. Pure music performance judged by listeners.' },
  { k: '04', icon: '🤝', title: 'Collabs', path: '/app/collabs', body: 'Connect with producers, vocalists, and songwriters. Split sheets, escrowed work, shared credits.' },
  { k: '05', icon: '👑', title: 'VIP Tiers', path: '/app/tiers', body: 'Ascend through 5 tiers. Unlock stems, custom mastering, music videos, and industry scouting.' },
];

const FEATURES = [
  { tag: 'Voice Engine', title: 'AI Vocals Over Your Lyrics', body: 'Singing and rap deliveries across Country, Reggae, Rock, Pop, Trap, Rap, and R&B.' },
  { tag: 'Month-Long Tournament', title: 'Up to 34 Entries • Weekly Eliminations', body: 'Artists battle throughout the month. The lowest scores drop off each week until only the Top 3 finalists advance.' },
  { tag: 'The Live Finale Show', title: 'Last Friday of the Month • 7 PM EST', body: 'Not a live battle—a live broadcast show introducing the Judges and Top 3 finalists, with live chat for audience participation and crowning the winner!' },
];

export function LandingPage() {
  const { currentUser, login, register } = useApp() as any;
  const navigate = useNavigate();
  const [destination, setDestination] = useState('/app');
  const openArea = (path: string) => {
    if (currentUser) navigate(path);
    else {
      setDestination(path);
      setError(null);
      setOpen('login');
    }
  };

  const [open, setOpen] = useState<Mode | null>(null);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<'artist' | 'fan' | 'judge'>('artist');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Guest Judge Application Form state
  const [judgeAppName, setJudgeAppName] = useState('');
  const [judgeAppEmail, setJudgeAppEmail] = useState('');
  const [judgeMusicExp, setJudgeMusicExp] = useState('');
  const [judgeReason, setJudgeReason] = useState('');
  const [judgeGenre, setJudgeGenre] = useState('RAP');
  const [judgeFairness, setJudgeFairness] = useState('');
  const [agreeUnpaid, setAgreeUnpaid] = useState(false);
  const [judgeSuccessMsg, setJudgeSuccessMsg] = useState<string | null>(null);

  const [ageGroup, setAgeGroup] = useState<'18plus' | '13to17' | 'under13' | ''>('');
  const [parentalPassword, setParentalPassword] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeAiPolicy, setAgreeAiPolicy] = useState(false);
  const [agreeContentRules, setAgreeContentRules] = useState(false);

  const needsParentalConsent = ageGroup === '13to17';
  const ageBlocked = ageGroup === 'under13';
  const canRegister =
    username.trim().length >= 3 &&
    email.trim().includes('@') &&
    password.length >= 6 &&
    ageGroup !== '' &&
    !ageBlocked &&
    (!needsParentalConsent || parentalPassword.length >= 6) &&
    agreeTerms &&
    agreePrivacy &&
    agreeAiPolicy &&
    agreeContentRules;

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (open === 'login') {
        await login(username.trim(), password);
      } else {
        const consent = {
          ageGroup: ageGroup || '18plus',
          termsAccepted: agreeTerms,
          privacyAccepted: agreePrivacy,
          aiPolicyAccepted: agreeAiPolicy,
          contentRulesAccepted: agreeContentRules,
          parentalConsent: needsParentalConsent,
          parentalPassword: needsParentalConsent ? parentalPassword : undefined,
        };

        const registrationEmail = email.trim() || `${username.trim()}@iam-muzik.com`;
        if (typeof register === 'function') {
          await register(username.trim(), registrationEmail, password, consent);
        } else if ((api as any).register) {
          await (api as any).register(username.trim(), registrationEmail, password, consent);
        }

        await login(username.trim(), password);
      }
      setOpen(null);
      const {user} = await api.me();
      navigate(destination === '/app' ? user.role === 'ADMIN' ? '/app/admin' : user.role === 'JUDGE' ? '/app/judge' : '/app' : destination);
    } catch (err: any) {
      setError(err?.message || 'Authentication failed. Please verify your credentials.');
    } finally {
      setBusy(false);
    }
  };

  const handleGuestJudgeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!agreeUnpaid) {
      setError('You must acknowledge that Guest Judge positions are voluntary and non-paid.');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch('/api/guest-judge/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: judgeAppName,
          email: judgeAppEmail,
          genre: judgeGenre,
          reason: judgeReason,
          musicExp: judgeMusicExp,
          fairness: judgeFairness,
          agreeUnpaid,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Failed to submit application');
      setJudgeSuccessMsg('Your Guest Judge application was saved and sent to the admin’s notifications.');
    } catch (err: any) {
      setError(err?.message || 'Could not send application.');
    } finally {
      setBusy(false);
    }
  };



  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100%',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        backgroundImage: "url('/bg/home.jpg')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        color: '#fff',
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <header
        className="landing-public-header"
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 36px',
          background: 'rgba(5, 10, 25, 0.25)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          borderBottom: '1px solid rgba(212, 175, 55, 0.25)',
        }}
      >
        <div
          onClick={() => navigate('/')}
          style={{
            cursor: 'pointer',
            fontSize: '24px',
            fontWeight: 900,
            letterSpacing: '3px',
            textTransform: 'uppercase',
            color: '#fff',
            textShadow: '0 2px 10px rgba(0,0,0,0.9)',
          }}
        >
          IAM<span style={{ color: '#d4af37' }}>-MUZIK</span>
        </div>

        <nav className="landing-public-nav" style={{ display: 'flex', alignItems: 'center', gap: '26px' }}>
          <a href="#ecosystem" onClick={(e) => { e.preventDefault(); document.getElementById('ecosystem')?.scrollIntoView({behavior:'smooth'}); }} style={{ color: '#ffffff', textDecoration: 'none', fontSize: '15px', fontWeight: 700, textShadow: '0 2px 6px #000' }}>Ecosystem</a>
          <button onClick={() => openArea('/app/studio')} style={{ background: 'none', border: 'none', color: '#ffffff', fontSize: '15px', fontWeight: 700, cursor: 'pointer', textShadow: '0 2px 6px #000' }}>Studio</button>
          <button onClick={() => openArea('/app/arena')} style={{ background: 'none', border: 'none', color: '#ffffff', fontSize: '15px', fontWeight: 700, cursor: 'pointer', textShadow: '0 2px 6px #000' }}>Arena</button>
          <button onClick={() => openArea('/app/wave')} style={{ background: 'none', border: 'none', color: '#ffffff', fontSize: '15px', fontWeight: 700, cursor: 'pointer', textShadow: '0 2px 6px #000' }}>Feed</button>
          <button onClick={() => navigate('/wave-radio')} style={{ background: 'none', border: 'none', color: '#ffffff', fontSize: '15px', fontWeight: 700, cursor: 'pointer', textShadow: '0 2px 6px #000' }}>Wave Radio</button>
          <button onClick={() => openArea('/app/tiers')} style={{ background: 'none', border: 'none', color: '#ffffff', fontSize: '15px', fontWeight: 700, cursor: 'pointer', textShadow: '0 2px 6px #000' }}>VIP Tiers</button>
        </nav>

        <div className="landing-public-actions" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button
            onClick={() => { setError(null); setJudgeSuccessMsg(null); setOpen('guest_judge_apply'); }}
            style={{
              padding: '8px 18px',
              borderRadius: '999px',
              border: '1px solid rgba(212, 175, 55, 0.75)',
              background: 'rgba(10, 24, 45, 0.40)',
              color: '#d4af37',
              fontWeight: 800,
              fontSize: '13px',
              cursor: 'pointer',
              backdropFilter: 'blur(6px)',
              textShadow: '0 1px 4px #000',
            }}
          >
            ⚖️ Guest Judge Application
          </button>
          <button
            onClick={() => { setError(null); setOpen('login'); }}
            style={{
              background: 'rgba(15, 23, 42, 0.40)',
              border: '1px solid rgba(255, 255, 255, 0.35)',
              borderRadius: '999px',
              color: '#ffffff',
              fontWeight: 700,
              fontSize: '13px',
              cursor: 'pointer',
              padding: '8px 18px',
              backdropFilter: 'blur(6px)',
              textShadow: '0 1px 4px #000',
            }}
          >
            Log in
          </button>
          <button
            onClick={() => { setError(null); setRole('artist'); setOpen('register'); }}
            style={{
              padding: '8px 20px',
              borderRadius: '999px',
              border: '1px solid rgba(212, 175, 55, 0.9)',
              background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.5), rgba(124, 92, 255, 0.6))',
              color: '#fff',
              fontWeight: 800,
              fontSize: '13px',
              cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(6px)',
            }}
          >
            Sign up
          </button>
        </div>
      </header>

      {/* Hero Section with Ultra-Sheer Glass Bubble */}
      <section
        style={{
          position: 'relative',
          zIndex: 10,
          minHeight: '78vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 24px',
        }}
      >
        <div
          style={{
            maxWidth: '660px',
            width: '100%',
            background: 'rgba(10, 25, 50, 0.08)',
            border: '1px solid rgba(212, 175, 55, 0.45)',
            boxShadow: '0 16px 40px rgba(0, 0, 0, 0.35), inset 0 0 16px rgba(212, 175, 55, 0.12)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            borderRadius: '26px',
            padding: '44px 32px',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '18px',
          }}
        >
          <div
            style={{
              fontSize: '12px',
              fontWeight: 900,
              letterSpacing: '3px',
              textTransform: 'uppercase',
              color: '#d4af37',
              textShadow: '0 2px 8px #000',
            }}
          >
            I AM the Music • Tournaments & Live Finale
          </div>

          <h1
            style={{
              fontSize: 'clamp(30px, 4.8vw, 46px)',
              fontWeight: 900,
              lineHeight: 1.15,
              margin: 0,
              color: '#ffffff',
              textShadow: '0 3px 18px rgba(0,0,0,0.95)',
            }}
          >
            Turn Your Lyrics & Sound Into Reality
          </h1>

          <p
            style={{
              fontSize: '16px',
              lineHeight: 1.6,
              color: '#f0f4fc',
              maxWidth: '540px',
              margin: 0,
              textShadow: '0 2px 8px rgba(0,0,0,0.95)',
            }}
          >
            Month-long battles up to 34 entries with weekly eliminations. Top 3 finalists advance to the 4th-week live finale show on the last Friday of each month at 7 PM EST!
          </p>

          <div style={{ marginTop: '10px', display: 'flex', gap: '14px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <button
              onClick={() => openArea('/app/studio')}
              style={{
                padding: '14px 36px',
                borderRadius: '999px',
                border: '1px solid rgba(212, 175, 55, 0.95)',
                background: 'linear-gradient(135deg, #d4af37 0%, #aa8529 100%)',
                color: '#0a0d14',
                fontWeight: 900,
                fontSize: '15px',
                cursor: 'pointer',
                boxShadow: '0 6px 22px rgba(212, 175, 55, 0.55)',
              }}
            >
              Enter the Studio
            </button>
            <button
              onClick={() => openArea('/app/arena')}
              style={{
                padding: '14px 28px',
                borderRadius: '999px',
                border: '1px solid rgba(255, 255, 255, 0.45)',
                background: 'rgba(15, 23, 42, 0.45)',
                color: '#ffffff',
                fontWeight: 800,
                fontSize: '15px',
                cursor: 'pointer',
                backdropFilter: 'blur(6px)',
                textShadow: '0 2px 6px #000',
              }}
            >
              Enter Battle Arena
            </button>
          </div>
        </div>
      </section>

      {/* The Ecosystem Section */}
      <section
        id="ecosystem"
        style={{
          position: 'relative',
          zIndex: 10,
          padding: '60px 24px',
          maxWidth: '1200px',
          margin: '0 auto',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '44px' }}>
          <span style={{ fontSize: '13px', fontWeight: 900, letterSpacing: '3px', textTransform: 'uppercase', color: '#d4af37', textShadow: '0 2px 6px #000' }}>
            The Ecosystem
          </span>
          <h2 style={{ fontSize: '36px', fontWeight: 900, margin: '8px 0', textShadow: '0 3px 14px rgba(0,0,0,0.95)' }}>
            Five Pillars. One Culture.
          </h2>
          <p style={{ color: '#f0f4fc', maxWidth: '620px', margin: '0 auto', fontSize: '16px', textShadow: '0 2px 6px #000' }}>
            Month-long tournament eliminations, certified judge panels, and the live finale show introducing our finalists and winners.
          </p>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '20px',
          }}
        >
          {PILLARS.map((p) => (
            <div
              key={p.k}
              onClick={() => openArea(p.path)}
              style={{
                cursor: 'pointer',
                background: 'rgba(10, 25, 50, 0.12)',
                border: '1px solid rgba(212, 175, 55, 0.35)',
                borderRadius: '18px',
                padding: '28px 22px',
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                transition: 'transform 0.15s ease',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '28px' }}>{p.icon}</span>
                <span style={{ fontSize: '12px', fontWeight: 900, color: '#d4af37', letterSpacing: '1px' }}>{p.k}</span>
              </div>
              <h3 style={{ fontSize: '20px', fontWeight: 800, margin: 0, color: '#fff', textShadow: '0 2px 6px #000' }}>{p.title}</h3>
              <p style={{ fontSize: '14px', lineHeight: 1.6, color: '#e6eeff', margin: 0, textShadow: '0 1px 4px #000' }}>{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Feature Spotlight */}
      <section
        style={{
          position: 'relative',
          zIndex: 10,
          padding: '40px 24px 60px',
          maxWidth: '1200px',
          margin: '0 auto',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px',
          }}
        >
          {FEATURES.map((f) => (
            <div
              key={f.title}
              style={{
                background: 'rgba(10, 20, 44, 0.12)',
                border: '1px solid rgba(255, 255, 255, 0.20)',
                borderRadius: '16px',
                padding: '24px',
                backdropFilter: 'blur(4px)',
              }}
            >
              <span style={{ fontSize: '11px', fontWeight: 900, color: '#d4af37', textTransform: 'uppercase', letterSpacing: '2px', textShadow: '0 1px 4px #000' }}>{f.tag}</span>
              <h3 style={{ fontSize: '19px', fontWeight: 800, margin: '8px 0', color: '#fff', textShadow: '0 2px 6px #000' }}>{f.title}</h3>
              <p style={{ fontSize: '14px', lineHeight: 1.6, color: '#e6eeff', margin: 0, textShadow: '0 1px 4px #000' }}>{f.body}</p>
            </div>
          ))}
        </div>

        {/* Live Broadcast Breakdown Card */}
        <div
          style={{
            marginTop: '32px',
            background: 'rgba(10, 25, 50, 0.16)',
            border: '1px solid rgba(212, 175, 55, 0.45)',
            borderRadius: '18px',
            padding: '24px 28px',
            backdropFilter: 'blur(6px)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <span style={{ fontSize: '28px' }}>🎤️</span>
            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 900, color: '#d4af37', textShadow: '0 2px 6px #000' }}>
              The 4th Week Live Finale Show (Last Friday of the Month • 7 PM EST)
            </h3>
          </div>
          <p style={{ fontSize: '14px', lineHeight: 1.7, color: '#f0f4fc', margin: 0, textShadow: '0 1px 4px #000' }}>
            The finale is <strong>not a live battle</strong> (live battles will come later). It is a live broadcast show streaming at <strong>7:00 PM EST on the last Friday of each month</strong>. We introduce the judges, spotlight the <strong>Top 3 Finalists</strong>, explore the artist stories, and crown the tournament winner live with full audience live chat!
          </p>
        </div>

        {/* AI Policy */}
        <div
          style={{
            marginTop: '20px',
            background: 'rgba(10, 25, 50, 0.12)',
            border: '1px solid rgba(212, 175, 55, 0.40)',
            borderRadius: '16px',
            padding: '20px 24px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            backdropFilter: 'blur(4px)',
          }}
        >
          <span style={{ fontSize: '26px' }}>🎤</span>
          <div style={{ fontSize: '14px', lineHeight: 1.5, color: '#f0f4fc', textShadow: '0 1px 4px #000' }}>
            <strong style={{ color: '#d4af37' }}>{AI_ONE_LINER}</strong> — {AI_ROLE_EXPLANATION}
          </div>
        </div>
      </section>

      {/* Footer Navigation */}
      <footer
        style={{
          position: 'relative',
          zIndex: 10,
          padding: '24px 24px 36px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '14px',
          background: 'rgba(5, 8, 18, 0.40)',
          borderTop: '1px solid rgba(212, 175, 55, 0.25)',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '12px',
            maxWidth: '860px',
            width: '100%',
          }}
        >
          {[
            { label: '🎹 Studio', path: '/app/studio' },
            { label: '⚔️ Artist Battles', path: '/app/arena' },
            { label: '⚖️ Judge Arena', path: '/app/judge' },
            { label: '🔥 Feed', path: '/app/wave' },
            { label: '📻 Wave Radio', path: '/wave-radio', public: true },
            { label: '👑 VIP Tiers', path: '/app/tiers' },
          ].map((item) => (
            <button
              key={item.path}
              onClick={() => item.public ? navigate(item.path) : openArea(item.path)}
              style={{
                background: 'rgba(10, 24, 45, 0.35)',
                border: '1px solid rgba(212, 175, 55, 0.35)',
                borderRadius: '12px',
                padding: '10px 20px',
                color: '#f0f4fc',
                fontSize: '14px',
                fontWeight: 700,
                cursor: 'pointer',
                backdropFilter: 'blur(6px)',
                textShadow: '0 1px 4px #000',
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
        <Footer />
      </footer>

      {/* Modals */}
      {open === 'guest_judge_apply' && (
        <div
          className="modal-overlay"
          onClick={() => setOpen(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.80)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px',
          }}
        >
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '540px',
              width: '100%',
              background: '#0d131f',
              border: '1px solid rgba(212, 175, 55, 0.6)',
              borderRadius: '20px',
              padding: '30px',
              color: '#fff',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#d4af37' }}>
                  ⚖️ Guest Judge Application
                </h2>
                <div style={{ fontSize: '12px', color: '#aaa', marginTop: '4px' }}>
                  Fan & Creator Interaction Questionnaire
                </div>
              </div>
              <button onClick={() => setOpen(null)} style={{ background: 'none', border: 'none', color: '#999', fontSize: '24px', cursor: 'pointer' }}>×</button>
            </div>

            {judgeSuccessMsg ? (
              <div style={{ textAlign: 'center', padding: '24px 10px' }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>🎖️</div>
                <h3 style={{ color: '#d4af37', marginBottom: '8px' }}>Application Submitted</h3>
                <p style={{ color: '#ddd', fontSize: '14px', lineHeight: 1.6 }}>{judgeSuccessMsg}</p>
                <button
                  onClick={() => setOpen(null)}
                  style={{
                    marginTop: '16px',
                    padding: '10px 24px',
                    borderRadius: '8px',
                    background: '#d4af37',
                    color: '#000',
                    fontWeight: 700,
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  Back to IAM-MUZIK
                </button>
              </div>
            ) : (
              <form onSubmit={handleGuestJudgeSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ background: 'rgba(212, 175, 55, 0.1)', border: '1px solid rgba(212, 175, 55, 0.3)', padding: '12px', borderRadius: '10px', fontSize: '13px', lineHeight: 1.5, color: '#e5d18e' }}>
                  ⚠️ <strong>Notice:</strong> Guest judging on IAM-MUZIK is a voluntary, prestige-based opportunity for music community members. <strong>Guest judges will not receive financial compensation</strong>.
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#ccc' }}>Full Name or Community Alias</label>
                  <input
                    type="text"
                    required
                    value={judgeAppName}
                    onChange={(e) => setJudgeAppName(e.target.value)}
                    placeholder="Your name or stage handle"
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', background: '#161e2e', border: '1px solid #2a374f', color: '#fff', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#ccc' }}>Email Address</label>
                  <input
                    type="email"
                    required
                    value={judgeAppEmail}
                    onChange={(e) => setJudgeAppEmail(e.target.value)}
                    placeholder="email@example.com"
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', background: '#161e2e', border: '1px solid #2a374f', color: '#fff', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#ccc' }}>Primary Genre Expertise</label>
                  <select
                    value={judgeGenre}
                    onChange={(e) => setJudgeGenre(e.target.value)}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', background: '#161e2e', border: '1px solid #2a374f', color: '#fff', boxSizing: 'border-box' }}
                  >
                    <option value="RAP">Rap</option>
                    <option value="TRAP">Trap</option>
                    <option value="R_AND_B">R&B</option>
                    <option value="POP">Pop</option>
                    <option value="ROCK_N_ROLL">Rock 'n' Roll</option>
                    <option value="REGGAE">Reggae</option>
                    <option value="COUNTRY">Country</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#ccc' }}>
                    1. Why should IAM-MUZIK consider you to be a Guest Judge?
                  </label>
                  <textarea
                    required
                    rows={3}
                    value={judgeReason}
                    onChange={(e) => setJudgeReason(e.target.value)}
                    placeholder="What gives you the ear, passion, and credibility to judge our artists?"
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', background: '#161e2e', border: '1px solid #2a374f', color: '#fff', boxSizing: 'border-box', fontFamily: 'inherit' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#ccc' }}>
                    2. Describe your musical background or experience listening to & analyzing music.
                  </label>
                  <textarea
                    required
                    rows={2}
                    value={judgeMusicExp}
                    onChange={(e) => setJudgeMusicExp(e.target.value)}
                    placeholder="e.g., active musician, songwriter, audio enthusiast, hardcore battle rap follower..."
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', background: '#161e2e', border: '1px solid #2a374f', color: '#fff', boxSizing: 'border-box', fontFamily: 'inherit' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#ccc' }}>
                    3. How do you maintain 100% fairness and avoid bias during intense artist battles?
                  </label>
                  <textarea
                    required
                    rows={2}
                    value={judgeFairness}
                    onChange={(e) => setJudgeFairness(e.target.value)}
                    placeholder="How do you score lyrics, delivery, flow, and stage presence impartially?"
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', background: '#161e2e', border: '1px solid #2a374f', color: '#fff', boxSizing: 'border-box', fontFamily: 'inherit' }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginTop: '6px' }}>
                  <input
                    type="checkbox"
                    id="unpaidAgree"
                    checked={agreeUnpaid}
                    onChange={(e) => setAgreeUnpaid(e.target.checked)}
                    required
                    style={{ marginTop: '3px' }}
                  />
                  <label htmlFor="unpaidAgree" style={{ fontSize: '13px', color: '#ccc', cursor: 'pointer', lineHeight: 1.4 }}>
                    I understand and agree that this is an unpaid guest judging role focused on music community participation and discovering top talent.
                  </label>
                </div>

                {error && <div style={{ color: '#ff6b6b', fontSize: '13px' }}>{error}</div>}

                <button
                  type="submit"
                  disabled={busy}
                  style={{
                    marginTop: '8px',
                    padding: '12px',
                    borderRadius: '10px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #d4af37 0%, #aa8529 100%)',
                    color: '#000',
                    fontWeight: 800,
                    fontSize: '15px',
                    cursor: busy ? 'not-allowed' : 'pointer',
                  }}
                >
                  {busy ? 'Submitting Application...' : 'Submit Guest Judge Application'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {(open === 'login' || open === 'register') && (
        <div
          className="modal-overlay"
          onClick={() => setOpen(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.75)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px',
          }}
        >
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '460px',
              width: '100%',
              background: '#0d131f',
              border: '1px solid rgba(212, 175, 55, 0.5)',
              borderRadius: '20px',
              padding: '28px',
              color: '#fff',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#d4af37' }}>
                {open === 'login' ? 'Log in to IAMMUZIK' : 'Create your account'}
              </h2>
              <button
                onClick={() => setOpen(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#999',
                  fontSize: '24px',
                  cursor: 'pointer',
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleAuth} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#ccc' }}>Username</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Your username"
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    background: '#161e2e',
                    border: '1px solid #2a374f',
                    color: '#fff',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {open === 'register' && (
                <div>
                  <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#ccc' }}>Email Address <span style={{ color: '#d4af37' }}>*</span></label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="youremail@example.com"
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      background: '#161e2e',
                      border: '1px solid #2a374f',
                      color: '#fff',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              )}

              <div>
                <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#ccc' }}>Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your password"
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    background: '#161e2e',
                    border: '1px solid #2a374f',
                    color: '#fff',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {open === 'register' && (
                <>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#ccc' }}>Display Name</label>
                    <input
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder="Stage or artist name"
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: '8px',
                        background: '#161e2e',
                        border: '1px solid #2a374f',
                        color: '#fff',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#ccc' }}>Primary Role</label>
                    <select
                      value={role}
                      onChange={(e) => setRole(e.target.value as any)}
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: '8px',
                        background: '#161e2e',
                        border: '1px solid #2a374f',
                        color: '#fff',
                        boxSizing: 'border-box',
                      }}
                    >
                      <option value="artist">🎤 Artist / Songwriter</option>
                      <option value="fan">🎧 Listener / Fan</option>
                      <option value="judge">⚖️ Battle Judge</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#ccc' }}>Age Verification</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="button"
                        onClick={() => setAgeGroup('18plus')}
                        style={{
                          flex: 1,
                          padding: '10px 6px',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          background: ageGroup === '18plus' ? '#7c5cff' : '#1a2233',
                          border: '1px solid #2a374f',
                          color: '#fff',
                        }}
                      >
                        18 or older
                      </button>
                      <button
                        type="button"
                        onClick={() => setAgeGroup('13to17')}
                        style={{
                          flex: 1,
                          padding: '10px 6px',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          background: ageGroup === '13to17' ? '#d4af37' : '#1a2233',
                          border: '1px solid #2a374f',
                          color: ageGroup === '13to17' ? '#000' : '#fff',
                        }}
                      >
                        13–17
                      </button>
                      <button
                        type="button"
                        onClick={() => setAgeGroup('under13')}
                        style={{
                          flex: 1,
                          padding: '10px 6px',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          background: ageGroup === 'under13' ? '#e53e3e' : '#1a2233',
                          border: '1px solid #2a374f',
                          color: '#fff',
                        }}
                      >
                        Under 13
                      </button>
                    </div>
                  </div>

                  {ageBlocked && (
                    <div style={{ color: '#ff6b6b', fontSize: '13px', background: 'rgba(255, 107, 107, 0.1)', padding: '10px', borderRadius: '8px' }}>
                      IAMMUZIK is for users 13 and older.
                    </div>
                  )}

                  {needsParentalConsent && (
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#ccc' }}>Parental Consent Password</label>
                      <input
                        type="password"
                        value={parentalPassword}
                        onChange={(e) => setParentalPassword(e.target.value)}
                        placeholder="Parent / Guardian password"
                        required
                        style={{
                          width: '100%',
                          padding: '12px',
                          borderRadius: '8px',
                          background: '#161e2e',
                          border: '1px solid #2a374f',
                          color: '#fff',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '12px', color: '#aaa', marginTop: '6px' }}>
                    <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', cursor: 'pointer' }}>
                      <input type="checkbox" checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)} />
                      <span>I agree to the Terms of Service.</span>
                    </label>
                    <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', cursor: 'pointer' }}>
                      <input type="checkbox" checked={agreePrivacy} onChange={(e) => setAgreePrivacy(e.target.checked)} />
                      <span>I agree to the Privacy Policy.</span>
                    </label>
                    <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', cursor: 'pointer' }}>
                      <input type="checkbox" checked={agreeAiPolicy} onChange={(e) => setAgreeAiPolicy(e.target.checked)} />
                      <span>I understand AI builds the beat and performs vocals, never writes lyrics.</span>
                    </label>
                    <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', cursor: 'pointer' }}>
                      <input type="checkbox" checked={agreeContentRules} onChange={(e) => setAgreeContentRules(e.target.checked)} />
                      <span>I agree to the Content Rules.</span>
                    </label>
                  </div>
                </>
              )}

              {error && (
                <div style={{ color: '#ff6b6b', fontSize: '13px' }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={busy || (open === 'register' && !canRegister)}
                style={{
                  marginTop: '10px',
                  padding: '14px',
                  borderRadius: '10px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #7c5cff 0%, #d4af37 100%)',
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: '15px',
                  cursor: busy || (open === 'register' && !canRegister) ? 'not-allowed' : 'pointer',
                  opacity: busy || (open === 'register' && !canRegister) ? 0.6 : 1,
                }}
              >
                {busy ? 'Processing...' : (open === 'login' ? 'Log In' : 'Create Free Account')}
              </button>
            </form>


          </div>
        </div>
      )}
    </div>
  );
}
